import React, { useState, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { CheckCircle, Warning, PaperPlaneRight, Paperclip, X } from "@phosphor-icons/react";

/**
 * Inline customer forms rendered in the public web chat widget when the bot
 * signals via `[[FORM:appointment]]`, `[[FORM:sales_lead]]`, or `[[FORM:parts_quote]]`.
 *
 * On WhatsApp/IG/Messenger the bot falls back to step-by-step Q&A.
 *
 * Feb 2026 (Sprint 30B — items #2, #3, #4).
 */

const DOC_RULES = {
  // Sprint 33I: relax cedula to accept hyphens ("001-1234567-8") or plain 11 digits.
  cedula:    { label: "Cédula",    len: [11, 13], regex: /^\d{3}-?\d{7}-?\d{1}$/, hint: "11 dígitos (con o sin guiones)" },
  rnc:       { label: "RNC",       len: [9, 11],  regex: /^\d{1,3}-?\d{5,7}-?\d?$/, hint: "9 dígitos (con o sin guiones)" },
  pasaporte: { label: "Pasaporte", len: [6, 12],  regex: /^[A-Z0-9]{6,12}$/i, hint: "6-12 caracteres" },
};

// 32 provincias RD (incluye Distrito Nacional).
const PROVINCIAS = [
  "Distrito Nacional", "Azua", "Baoruco", "Barahona", "Dajabón", "Duarte",
  "Elías Piña", "El Seibo", "Espaillat", "Hato Mayor", "Hermanas Mirabal",
  "Independencia", "La Altagracia", "La Romana", "La Vega", "María Trinidad Sánchez",
  "Monseñor Nouel", "Monte Cristi", "Monte Plata", "Pedernales", "Peravia",
  "Puerto Plata", "Samaná", "Sánchez Ramírez", "San Cristóbal", "San José de Ocoa",
  "San Juan", "San Pedro de Macorís", "Santiago", "Santiago Rodríguez",
  "Santo Domingo", "Valverde",
];

const BRANCHES = ["Kennedy", "Luperón", "Santiago", "Punta Cana", "RapidCentro"];
const APPOINTMENT_TYPES = ["Mantenimiento", "Mantenimiento y Chequeo", "Chequeo/Reparación", "Recall", "Campaña"];

function Input({ value, onChange, placeholder, testid, className = "", ...rest }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className={`w-full h-9 rounded-md border border-input bg-background px-2.5 text-sm ${className}`}
      {...rest}
    />
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-9 rounded-md border border-input bg-background px-2.5 text-sm"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function ChatForm({ formType, conversationId, onSubmitted }) {
  // Back-compat: old 'quote' marker maps to sales_lead.
  const type = formType === "quote" ? "sales_lead" : formType;

  const [state, setState] = useState({
    name: "", doc_type: "cedula", document: "",
    email: "", provincia: "",
    // Sprint 34K — CRM webhook flow (nuevos + seminuevos): last_name + phone required
    last_name: "", phone: "",
    brand: "", model: "", year: "",
    appointment_type: "", branch: "", preferred_date: "", preferred_time: "",
    // Sprint 33H — appointment enhancements from QA feedback
    issue_description: "",  // "¿cuál es el inconveniente que presenta su vehículo?"
    working_hours: "",      // "¿cuál es el horario en que labora?"
    chassis: "", part_number: "", part_description: "",
    // owner_transfer (Sprint 33D)
    matricula: "", address: "", phone: "",
    // tire_quote (Sprint 33H)
    tire_size: "",
    // attachments (owner_transfer only)
    attachments: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const patch = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const docErr = useMemo(() => {
    if (!state.document) return null;
    const rule = DOC_RULES[state.doc_type];
    return rule.regex.test(state.document.trim()) ? null : `Debe tener ${rule.hint}`;
  }, [state.doc_type, state.document]);

  const canSubmit = (() => {
    if (state.name.trim().length < 2) return false;
    if (type !== "sales_lead" && docErr) return false;
    if (type !== "sales_lead" && state.document.trim().length < 6) return false;
    if (type === "sales_lead") {
      // Sprint 34K — 5 campos requeridos que van al webhook Zapier → Dynamics CRM
      return state.last_name.trim().length >= 2 &&
             state.brand.trim().length > 1 && state.model.trim().length > 0 &&
             state.phone.trim().replace(/\D/g, "").length >= 10 &&
             state.email.includes("@") &&
             state.branch;
    }
    if (type === "appointment") {
      // Sprint 35G — issue_description is required for "Chequeo/Reparación",
      // "Recall" and "Campaña"; pure "Mantenimiento" and "Mantenimiento y Chequeo"
      // accept an empty description (routine service).
      const needsIssue = state.appointment_type && !["Mantenimiento", "Mantenimiento y Chequeo"].includes(state.appointment_type);
      return state.brand.trim().length > 1 && state.model.trim().length > 0 && state.year &&
             state.appointment_type && state.branch && state.preferred_date && state.preferred_time &&
             (!needsIssue || state.issue_description.trim().length >= 5);
    }
    if (type === "parts_quote") {
      return state.brand.trim().length > 1 && state.model.trim().length > 0 && state.year &&
             state.part_description.trim().length > 3 && (state.chassis.trim().length > 3 || state.part_number.trim().length > 0);
    }
    if (type === "owner_transfer") {
      return state.matricula.trim().length >= 4 &&
             state.address.trim().length >= 6 &&
             state.phone.trim().length >= 7 &&
             state.email.includes("@");
    }
    if (type === "tire_quote") {
      return state.brand.trim().length > 1 && state.model.trim().length > 0 && state.year &&
             state.tire_size.trim().length >= 4;
    }
    return false;
  })();

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setErr(null);
    try {
      const payload = { form_type: type, data: state };
      if (type === "owner_transfer") {
        payload.attachments = (state.attachments || []).map((a) => a.id);
      }
      await axios.post(
        `${API_BASE}/public/conversations/${conversationId}/form-submission`,
        payload,
        { timeout: 15000 },
      );
      setDone(true);
      onSubmitted?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error enviando el formulario.");
    } finally { setSubmitting(false); }
  };

  // Owner-transfer only: upload one file to /public/files, then push its id into state.attachments.
  const uploadAttachment = async (file) => {
    if (!file) return;
    if ((state.attachments || []).length >= 6) {
      setErr("Máximo 6 archivos por solicitud.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("conversation_id", conversationId);
    fd.append("kind", "owner_transfer_doc");
    try {
      const { data } = await axios.post(`${API_BASE}/public/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      setState((s) => ({ ...s, attachments: [...(s.attachments || []), { id: data.id, filename: data.filename, size: data.size_bytes }] }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo subir el archivo.");
    }
  };

  const removeAttachment = (id) => {
    setState((s) => ({ ...s, attachments: (s.attachments || []).filter((a) => a.id !== id) }));
  };

  if (done) {
    return (
      <div className="my-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm" data-testid="chat-form-done">
        <CheckCircle size={20} weight="fill" className="text-emerald-500 inline mr-1.5" />
        <span className="font-medium text-emerald-700 dark:text-emerald-400">¡Gracias! Datos recibidos.</span>
        <div className="text-xs text-muted-foreground mt-1">Un asesor te contactará por WhatsApp o email en breve.</div>
      </div>
    );
  }

  const rule = DOC_RULES[state.doc_type];
  const title = {
    appointment: "Cita de Taller — Tus datos",
    sales_lead: "Solicitud de contacto comercial",
    parts_quote: "Cotización de Repuestos",
    owner_transfer: "Solicitud de cambio de propietario",
    tire_quote: "Cotización de Gomas / Neumáticos",
  }[type] || "Tus datos";

  return (
    <div className="my-3 p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-2" data-testid={`chat-form-${type}`}>
      <div className="text-xs font-semibold text-primary uppercase tracking-wider">{title}</div>

      {/* Nombre — común a todos */}
      <Input value={state.name} onChange={(v) => patch("name", v)}
        placeholder={type === "sales_lead" ? "Nombre" : "Nombre completo"} testid="form-name" />

      {/* Sprint 34K — Apellidos solo en sales_lead (matching prod form) */}
      {type === "sales_lead" && (
        <Input value={state.last_name} onChange={(v) => patch("last_name", v)}
          placeholder="Apellidos" testid="form-last-name" />
      )}

      {/* ID: cédula/rnc/pasaporte — todos EXCEPTO sales_lead (Sprint 34K) */}
      {type !== "sales_lead" && (
        <>
          <div className="flex gap-1.5" data-testid="form-doctype">
            {(["cedula", "rnc", "pasaporte"]).map((t) => (
              <button key={t} type="button" onClick={() => setState((s) => ({ ...s, doc_type: t, document: "" }))}
                data-testid={`form-doctype-${t}`}
                className={`flex-1 h-8 rounded-md text-[11px] font-medium border transition-colors ${
                  state.doc_type === t ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:bg-secondary"
                }`}>
                {DOC_RULES[t].label}
              </button>
            ))}
          </div>
          <div>
            <Input value={state.document} onChange={(v) => patch("document", v)}
              placeholder={`Número (${rule.hint})`} testid="form-document"
              className={`font-mono ${docErr ? "border-rose-500" : ""}`} />
            {docErr && (
              <div className="text-[10px] text-rose-500 mt-0.5 flex items-center gap-1">
                <Warning size={10} weight="fill" />{docErr}
              </div>
            )}
          </div>
        </>
      )}

      {/* Sales Lead only — 5 required fields → Zapier → Dynamics CRM */}
      {type === "sales_lead" && (
        <>
          <Input value={state.phone} onChange={(v) => patch("phone", v)}
            placeholder="Número de contacto (ej. 8095555555)" testid="form-phone"
            type="tel" inputMode="numeric" />
          <Input value={state.email} onChange={(v) => patch("email", v)}
            placeholder="Correo electrónico" testid="form-email" type="email" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={state.brand} onChange={(v) => patch("brand", v)} placeholder="Marca de interés" testid="form-brand-sales" />
            <Input value={state.model} onChange={(v) => patch("model", v)} placeholder="Modelo de interés" testid="form-model-sales" />
          </div>
          <Select value={state.branch} onChange={(v) => patch("branch", v)}
            options={BRANCHES} placeholder="Sucursal — Elige una opción"
            testid="form-branch-sales" />
          <label className="flex items-center gap-2 text-xs" data-testid="form-test-drive">
            <input type="checkbox" checked={!!state.test_drive_interest}
              onChange={(e) => patch("test_drive_interest", e.target.checked)} data-testid="form-test-drive-checkbox" />
            Quiero hacer un test drive
          </label>
        </>
      )}

      {/* Appointment only */}
      {type === "appointment" && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Input value={state.brand} onChange={(v) => patch("brand", v)} placeholder="Marca" testid="form-brand" />
            <Input value={state.model} onChange={(v) => patch("model", v)} placeholder="Modelo" testid="form-model" />
            <Input value={state.year} onChange={(v) => patch("year", v)} placeholder="Año" testid="form-year" inputMode="numeric" />
          </div>
          <Select value={state.appointment_type} onChange={(v) => patch("appointment_type", v)} options={APPOINTMENT_TYPES}
            placeholder="Tipo de cita" testid="form-appt-type" />
          <Select value={state.branch} onChange={(v) => patch("branch", v)} options={BRANCHES}
            placeholder="Taller de preferencia" testid="form-branch" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={state.preferred_date} onChange={(v) => patch("preferred_date", v)}
              testid="form-date" min={new Date().toISOString().slice(0, 10)} placeholder="Día" />
            <Input type="time" value={state.preferred_time} onChange={(v) => patch("preferred_time", v)}
              testid="form-time" placeholder="Hora" min="07:00" max="16:00" />
          </div>
          {/* Sprint 33H — QA feedback: capture issue + working hours */}
          <textarea
            value={state.issue_description}
            onChange={(e) => patch("issue_description", e.target.value)}
            placeholder={["Mantenimiento", "Mantenimiento y Chequeo"].includes(state.appointment_type)
              ? "Notas del mantenimiento (opcional)"
              : "¿Cuál es el inconveniente que presenta su vehículo?"}
            data-testid="form-issue"
            rows={2}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm resize-none"
          />
          <Input
            value={state.working_hours}
            onChange={(v) => patch("working_hours", v)}
            placeholder="Horario en que labora (para coordinar la entrega)"
            testid="form-work-hours"
          />
        </>
      )}

      {/* Tire Quote — Sprint 33H — RapidCentro only */}
      {type === "tire_quote" && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Input value={state.brand} onChange={(v) => patch("brand", v)} placeholder="Marca del vehículo" testid="form-brand" />
            <Input value={state.model} onChange={(v) => patch("model", v)} placeholder="Modelo" testid="form-model" />
            <Input value={state.year} onChange={(v) => patch("year", v)} placeholder="Año" testid="form-year" inputMode="numeric" />
          </div>
          <Input
            value={state.tire_size}
            onChange={(v) => patch("tire_size", v)}
            placeholder="Numeración de la goma (ej: 205/55R16)"
            testid="form-tire-size"
            className="font-mono uppercase"
          />
          <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-2.5 text-[11px] text-orange-800 dark:text-orange-200">
            Los neumáticos son cotizados vía nuestro taller <b>RapidCentro</b>. Enviaremos tus
            datos y te llamarán al mismo número de contacto.
          </div>
        </>
      )}

      {/* Parts Quote only */}
      {type === "parts_quote" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Input value={state.chassis} onChange={(v) => patch("chassis", v)} placeholder="Chasis del vehículo" testid="form-chassis" className="font-mono uppercase" />
            <Input value={state.part_number} onChange={(v) => patch("part_number", v)} placeholder="Número de pieza" testid="form-part-number" className="font-mono" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input value={state.brand} onChange={(v) => patch("brand", v)} placeholder="Marca" testid="form-brand" />
            <Input value={state.model} onChange={(v) => patch("model", v)} placeholder="Modelo" testid="form-model" />
            <Input value={state.year} onChange={(v) => patch("year", v)} placeholder="Año" testid="form-year" inputMode="numeric" />
          </div>
          <textarea value={state.part_description} onChange={(e) => patch("part_description", e.target.value)}
            placeholder="Describe la pieza o repuesto que necesitas cotizar"
            data-testid="form-part-desc" rows={2}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm resize-none" />
        </>
      )}

      {/* Owner Transfer (Segundo Propietario) — Sprint 33D */}
      {type === "owner_transfer" && (
        <>
          <Input value={state.matricula} onChange={(v) => patch("matricula", v)} placeholder="Matrícula del vehículo" testid="form-matricula" className="font-mono uppercase" />
          <Input value={state.phone} onChange={(v) => patch("phone", v)} placeholder="Teléfono de contacto" testid="form-phone" type="tel" />
          <Input value={state.email} onChange={(v) => patch("email", v)} placeholder="Correo electrónico" testid="form-email" type="email" />
          <textarea value={state.address} onChange={(e) => patch("address", e.target.value)}
            placeholder="Dirección completa del nuevo propietario"
            data-testid="form-address" rows={2}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm resize-none" />

          {/* Attachments */}
          <div className="rounded-md border border-dashed border-primary/30 bg-background/60 p-2.5" data-testid="form-attachments">
            <div className="text-[11px] font-medium mb-1.5 flex items-center gap-1.5 text-primary">
              <Paperclip size={11} weight="fill" />
              Adjuntos (matrícula, cédula, contrato) — hasta 6 archivos
            </div>
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
              <input
                type="file"
                accept="application/pdf,image/*"
                multiple
                data-testid="form-attach-input"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((f) => uploadAttachment(f));
                  e.target.value = "";
                }}
              />
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-input bg-background hover:bg-secondary">
                <Paperclip size={11} /> Subir archivos
              </span>
              <span>PDF, JPG, PNG · hasta 15MB c/u</span>
            </label>
            {(state.attachments || []).length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="form-attach-list">
                {state.attachments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-[11px] bg-background rounded px-2 py-1 border border-border">
                    <span className="truncate">{a.filename} · {Math.round((a.size || 0) / 1024)} KB</span>
                    <button type="button" onClick={() => removeAttachment(a.id)} data-testid={`form-attach-remove-${a.id}`}
                      className="text-muted-foreground hover:text-rose-500 shrink-0">
                      <X size={11} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {err && <div className="text-xs text-rose-500">{err}</div>}
      <button type="button" onClick={submit} disabled={!canSubmit || submitting} data-testid="form-submit"
        className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5">
        <PaperPlaneRight size={13} weight="fill" />
        {submitting ? "Enviando…" : "Enviar y continuar"}
      </button>
    </div>
  );
}
